import React from 'react';
import { Card, Row, Col, Statistic, Space, Typography } from 'antd';
import {
  UserAddOutlined,
  DollarOutlined,
  TrophyOutlined,
  RiseOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { AppStatsCard } from './AppStatsCard';
import { RateRow } from './RateRow';
import styles from '../../pages/Statistics/statistics.module.css';

const { Text } = Typography;

interface ConversionStatsProps {
  data: {
    app_id: number;
    period: string;
    conversions: {
      registrations_total: number;
      registrations_from_push: number;
      deposits_total: number;
      deposits_from_push: number;
      registration_rate?: number;
      deposit_rate?: number;
    };
  };
}

const periodLabels: Record<string, string> = {
  '7d': 'последние 7 дней',
  '30d': 'последние 30 дней',
  '90d': 'последние 90 дней',
};

export const ConversionStats: React.FC<ConversionStatsProps> = ({ data }) => {
  const { conversions } = data;

  const registrationRate = conversions.registration_rate ?? 0;
  const depositRate = conversions.deposit_rate ?? 0;

  const totalConversions = conversions.registrations_total + conversions.deposits_total;
  const pushConversions = conversions.registrations_from_push + conversions.deposits_from_push;
  const overallAttribution = totalConversions > 0 ? (pushConversions / totalConversions) * 100 : 0;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={8}>
          <AppStatsCard
            title="Всего конверсий"
            value={totalConversions}
            icon={<RiseOutlined />}
            className={styles.info}
            compact
          />
        </Col>
        <Col xs={24} sm={8}>
          <AppStatsCard
            title="Из push"
            value={pushConversions}
            icon={<TrophyOutlined />}
            className={styles.success}
            compact
          />
        </Col>
        <Col xs={24} sm={8}>
          <AppStatsCard
            title="Общая атрибуция"
            value={`${overallAttribution.toFixed(2)}%`}
            icon={<CheckCircleOutlined />}
            compact
          />
        </Col>
      </Row>

      <Row gutter={[12, 12]}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <span className={styles.chartTitle}>
                <div className={styles.chartTitleIcon}>
                  <UserAddOutlined />
                </div>
                Регистрации
              </span>
            }
            className={styles.statsCard}
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Row gutter={12}>
                <Col span={12}>
                  <Statistic
                    title="Всего"
                    value={conversions.registrations_total}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Из push"
                    value={conversions.registrations_from_push}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Col>
              </Row>
              <RateRow label="Доля push в регистрациях" value={registrationRate} />
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <span className={styles.chartTitle}>
                <div className={styles.chartTitleIcon}>
                  <DollarOutlined />
                </div>
                Депозиты
              </span>
            }
            className={styles.statsCard}
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Row gutter={12}>
                <Col span={12}>
                  <Statistic
                    title="Всего"
                    value={conversions.deposits_total}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Из push"
                    value={conversions.deposits_from_push}
                    valueStyle={{ fontSize: 20 }}
                  />
                </Col>
              </Row>
              <RateRow label="Доля push в депозитах" value={depositRate} />
            </Space>
          </Card>
        </Col>
      </Row>

      <Text type="secondary" style={{ fontSize: 12 }}>
        Период: {periodLabels[data.period] ?? data.period}
      </Text>
    </Space>
  );
};